import React, { useState } from 'react';
import { Row, Col, Card, Select, Button } from 'antd';
import { UserOutlined, TeamOutlined, FileOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import styles from './team-performance.module.scss';
import { AnalyticsCard } from 'components/pages/crm-spot/shared-components';
import { useUserAnalytics } from 'components/pages/crm-spot/hooks/useUserAnalytics';
import { useToggle } from 'hooks/useToggle';
import { getDateRange } from 'components/pages/crm-spot/utils/get-date-range';

const TeamPerformanceBarChart = ({ leadsAssigned, leadsMatched }) => {
  const data = [
    { name: 'Leads Handled', value: leadsAssigned },
    { name: 'Leads Matched', value: leadsMatched },
  ];

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartContent}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="#0c5e79" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const TeamPerformance = ({ teamMembers }) => {
  const [userId, setUserId] = useState(teamMembers[0]?.user_id || null);
  const [isTeamView, toggleTeamView] = useToggle(true);
  const [range, setRange] = useState('week');
  const { from, to } = getDateRange(range);

  const { userAnalytics } = useUserAnalytics({
    from,
    to,
    user_id: isTeamView ? null : userId,
  });

  const rateSuccess =
    userAnalytics?.matched && userAnalytics?.assigned
      ? ((userAnalytics.matched / userAnalytics.assigned) * 100).toFixed(1)
      : 0;
  // Convert activeTimeMs (milliseconds) to hours and minutes
  const totalMinutes = Math.floor((userAnalytics?.activeTimeMs || 0) / 1000 / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const activeTime = `${hours}h ${minutes}m`;

  const queueActivity =
    userAnalytics?.teamAssigned && userAnalytics.assigned
      ? ((userAnalytics.assigned / userAnalytics.teamAssigned) * 100).toFixed(2)
      : 0;

  return (
    <div className={styles.teamPerformanceContainer}>
      <div className={styles.sectionHeader}>
        <UserOutlined className={styles.sectionIcon} />
        <h3 className={styles.sectionTitle}>Team Performance</h3>
      </div>

      <div className={styles.analyticsHeader}>
        <h2 className={styles.analyticsTitle}>User Analytics</h2>
        <div className={styles.filterControls}>
          <Select
            defaultValue={range}
            onChange={(value) => setRange(value)}
            className={styles.dateFilter}
            options={[
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'quarter', label: 'Last 3 Months' },
            ]}
          />

          <Button onClick={toggleTeamView} className={styles.teamViewButton} icon={<TeamOutlined />}>
            {isTeamView ? 'Team' : 'Individual'} Performance
          </Button>

          {!isTeamView && (
            <Select
              value={userId}
              className={styles.userFilter}
              onChange={setUserId}
              options={teamMembers.map((member) => ({
                value: member.user_id,
                label: `${member.first_name} ${member.last_name}`,
              }))}
            />
          )}

          <Button className={styles.exportButton} icon={<FileOutlined />} />
        </div>
      </div>

      <Row gutter={16} className={styles.metricsRow}>
        <Col span={6}>
          <AnalyticsCard title="Leads Handled" value={userAnalytics?.assigned} description="Total leads processed" />
        </Col>
        <Col span={6}>
          <AnalyticsCard title="Success Rate" value={`${rateSuccess}%`} description="Lead to match conversion" />
        </Col>
        <Col span={6}>
          <AnalyticsCard title="Active Time" value={activeTime} description="Hours active this month" />
        </Col>
        <Col span={6}>
          <AnalyticsCard title="Queue Activity" value={`${queueActivity}%`} description="Queue participation rate" />
        </Col>
      </Row>

      <Card className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>Lead Performance</h3>
          <p className={styles.chartSubtitle}>Individual lead handling metrics</p>
        </div>
        <TeamPerformanceBarChart leadsAssigned={userAnalytics?.assigned} leadsMatched={userAnalytics?.matched} />
      </Card>
    </div>
  );
};
